import { useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  useCancelJob,
  useDocuments,
  useIngest,
  useJobs,
  useStatusCounts,
  useSubmitJob,
  useTaxonomy,
  type DocumentStat,
  type JobOut,
} from "@/api/queries";
import { RunStateChip } from "@/components/run-state-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Label } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { statusFillClass } from "@/lib/status-tone";
import { cn, formatDate } from "@/lib/utils";

/**
 * DESIGN §14.1 — upload a document, then watch the work.
 *
 * ARCHITECTURE §8 invariant 01 is visible here more than anywhere else: the upload
 * returns a job id in a couple of hundred milliseconds, and everything expensive
 * happens in the worker. The Streamlit version ran the whole pipeline inside the
 * request and left the browser spinning for minutes.
 *
 * The job list polls itself while anything is queued or running, and stops when
 * nothing is — see `useJobs`.
 */
const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

const JOB_KINDS = [
  { kind: "pipeline", label: "Run pipeline", hint: "carry pending chunks through to review" },
  { kind: "rescan", label: "Rescan", hint: "every pair again under the current config (§9.3)" },
  { kind: "reembed", label: "Re-embed", hint: "rows whose embedding_model is not current (§5.6)" },
  { kind: "reenrich", label: "Re-enrich", hint: "rows whose enrichment version or prompt is stale" },
  { kind: "scan-stale", label: "Scan stale", hint: "approved → stale by version range (§7.6)" },
];

/**
 * One line of "where did this job get to".
 *
 * The cursor is real progress: the worker advances it with each committed batch, so a
 * resumed job says where it actually got to (§10.2).
 *
 * A probe keeps its whole report in `totals` (§14.4) because that is how the result
 * reaches the client. Printed raw it is a paragraph of JSON per row, which buries the
 * pipeline jobs this table exists for — so it gets counted instead, and the report
 * itself is read on the Probe screen.
 */
function progressSummary(job: JobOut): string {
  const totals = job.totals ?? {};
  if (job.kind === "probe") {
    const neighbours = (totals as { neighbours?: unknown }).neighbours;
    return Array.isArray(neighbours) ? `${neighbours.length} neighbours` : "—";
  }
  if (Object.keys(totals).length > 0) return JSON.stringify(totals).slice(0, 120);
  return job.cursor ? JSON.stringify(job.cursor) : "—";
}

/** A run's question-status breakdown as one bar, segment widths proportional to
 * count — the same colours as every `Badge` in the app, just without the text so a
 * whole row of runs can be scanned at once. */
function StackBar({ doc }: { doc: DocumentStat }) {
  if (doc.question_total === 0) {
    return <div className="bg-bank-surface-3 h-[7px] w-40 rounded-sm" />;
  }
  return (
    <div className="flex h-[7px] w-40 overflow-hidden rounded-sm">
      {Object.entries(doc.questions).map(([status, n]) =>
        n > 0 ? (
          <span
            key={status}
            className={statusFillClass(status)}
            style={{ width: `${(n / doc.question_total) * 100}%` }}
            title={`${status}: ${n}`}
          />
        ) : null,
      )}
    </div>
  );
}

export default function Ingest() {
  const [file, setFile] = useState<File | null>(null);
  const [force, setForce] = useState(false);
  const [expectedStack, setExpectedStack] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  const ingest = useIngest();
  const submit = useSubmitJob();
  const cancel = useCancelJob();
  const jobs = useJobs(15);
  const documents = useDocuments();
  const { data: counts } = useStatusCounts();
  const { data: stacks } = useTaxonomy("stack");

  function upload() {
    if (!file || !expectedStack) return;
    ingest.mutate(
      { file, force, expectedStack },
      {
        onSuccess: () => {
          setFile(null);
          setForce(false);
          setExpectedStack("");
          if (fileInput.current) fileInput.current.value = "";
        },
      },
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <h1 className="text-base font-semibold tracking-tight">Ingest</h1>

      <ErrorNote error={ingest.error ?? submit.error ?? cancel.error} />

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel>
          <PanelHeader title="Upload a document" />
          <div className="space-y-3 p-4">
            <div>
              <Label htmlFor="doc">
                <code>.docx</code> or <code>.txt</code> — DESIGN §1.1 takes nothing else
              </Label>
              <input
                id="doc"
                ref={fileInput}
                type="file"
                accept=".docx,.txt"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="file:border-bank-border-strong file:bg-bank-surface hover:file:bg-bank-surface-2 text-bank-muted w-full text-sm file:mr-3 file:rounded-md file:border file:px-2.5 file:py-1.5 file:text-sm file:font-medium"
              />
            </div>

            <div>
              <FieldLabel htmlFor="expected-stack">Tech stack (required)</FieldLabel>
              <Combobox
                id="expected-stack"
                value={expectedStack}
                onChange={setExpectedStack}
                options={(stacks ?? []).map((s) => ({ value: s.path, label: s.path }))}
              />
              <p className="text-bank-muted mt-1 text-xs">
                Tags every question in this file with this stack, so dedup only ever compares questions within
                the same stack (DESIGN §19.7 rec #7). Not stack-specific content? Use <code>general</code>.
                Don't see the stack you need? Add it on the <code>Stacks</code> page — it's usable here right
                away, no restart needed.
              </p>
            </div>

            <label className="text-bank-muted flex items-center gap-2 text-sm">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              Ingest again even if this file name was seen before
            </label>

            <Button variant="primary" disabled={!file || !expectedStack || ingest.isPending} onClick={upload}>
              {ingest.isPending ? "Uploading…" : "Ingest and queue the pipeline"}
            </Button>

            {ingest.data && (
              <Callout tone="good">
                {ingest.data.skipped ? (
                  <>
                    <strong>{ingest.data.source_doc}</strong> was already ingested. Tick the box to ingest it
                    again.
                  </>
                ) : (
                  <>
                    <strong>{ingest.data.source_doc}</strong>: {ingest.data.chars} chars →{" "}
                    {ingest.data.chunks} chunks.
                    {ingest.data.job ? ` Queued job #${ingest.data.job.job_id}.` : ""}
                  </>
                )}
              </Callout>
            )}

            <p className="text-bank-muted text-xs">
              Chunking happens in the request because it makes no model call. Everything after it — extract,
              enrich, embed, dedup, cluster — is a job the worker picks up (§8 invariant 01).
            </p>
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Run something" />
          <div className="space-y-2 p-4">
            {JOB_KINDS.map(({ kind, label, hint }) => (
              <div key={kind} className="flex items-center gap-3">
                <Button size="sm" disabled={submit.isPending} onClick={() => submit.mutate(kind)}>
                  {label}
                </Button>
                <span className="text-bank-muted text-xs">{hint}</span>
              </div>
            ))}
            <p className="text-bank-muted border-bank-border mt-3 border-t pt-3 text-xs">
              These spend model quota. The free tier is 20 requests per day per model (DESIGN §19.5 #7), and a
              job that runs out is requeued with its cursor rather than failed — check{" "}
              <code>qbank status</code> before starting a large one.
            </p>
          </div>
        </Panel>
      </div>

      <Panel>
        <PanelHeader
          title="Runs"
          actions={
            <span className="text-bank-muted text-xs">
              every document ingested — open one to watch it live, from extraction through review
            </span>
          }
        />
        {(documents.data?.items.length ?? 0) === 0 ? (
          <Empty>Nothing ingested yet.</Empty>
        ) : (
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border text-bank-muted sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>document</th>
                  <th className={TH}>uploaded</th>
                  <th className={cn(TH, "text-right")}>questions</th>
                  <th className={TH}>progress</th>
                  <th className={TH}>state</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {documents.data?.items.map((d) => (
                  <tr
                    key={d.source_doc}
                    className="border-bank-border hover:bg-bank-surface-2 border-t transition-colors"
                  >
                    <td className="max-w-50 truncate px-3 py-2 font-mono text-[11px]" title={d.source_doc}>
                      {d.source_doc}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {formatDate(d.uploaded_at)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                      {d.question_total}
                    </td>
                    <td className="px-3 py-2">
                      <StackBar doc={d} />
                    </td>
                    <td className="px-3 py-2">
                      <RunStateChip doc={d} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/ingest/runs/${encodeURIComponent(d.source_doc)}`}
                        className="text-bank-accent-ink text-xs font-medium hover:underline"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel className="flex min-h-0 flex-1 flex-col">
        <PanelHeader
          title="Jobs"
          actions={
            <span className="text-bank-muted text-xs">
              {counts
                ? `${Object.values(counts.counts).reduce((a, b) => a + b, 0)} questions in the bank`
                : ""}
            </span>
          }
        />
        <div className="min-h-0 flex-1 overflow-auto">
          {(jobs.data?.items.length ?? 0) === 0 ? (
            <Empty>No jobs yet.</Empty>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border text-bank-muted sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>#</th>
                  <th className={TH}>kind</th>
                  <th className={TH}>status</th>
                  <th className={TH}>started</th>
                  <th className={TH}>progress</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {jobs.data?.items.map((job) => (
                  <tr
                    key={job.id}
                    className="border-bank-border hover:bg-bank-surface-2 border-t align-top transition-colors"
                  >
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">{job.id}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{job.kind}</td>
                    <td className="px-3 py-2">
                      <Badge value={job.status} />
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {formatDate(job.started_at)}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {progressSummary(job)}
                      {job.error && (
                        <span className="text-bank-bad-ink block">{job.error.slice(0, 160)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {job.status === "queued" && (
                        <Button size="sm" disabled={cancel.isPending} onClick={() => cancel.mutate(job.id)}>
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
    </div>
  );
}
